/**
 * social-grouping — posts belong to the piece they amplify.
 *
 * FEAT-087 / S-378. The handoff's argument for the Social queue: "a post is
 * an artifact of a piece, not an independent object." A flat list of posts
 * sorted by time makes you reassemble that relationship in your head every
 * time you read it — three posts about one article look like three unrelated
 * jobs.
 *
 * Pure so the ordering rules are tested, and so the view does not carry
 * grouping logic it would then duplicate for another range.
 */

export interface SocialRowLike {
  id: string;
  piece_id: number | null;
  scheduled_at: string | null;
}

export interface SocialGroup<T extends SocialRowLike> {
  /** null = posts that amplify no piece — written directly, not derived. */
  pieceId: number | null;
  title: string;
  rows: T[];
  /** The earliest scheduled post in the group; null when none is scheduled. */
  earliest: string | null;
}

function earliestOf(rows: readonly SocialRowLike[]): string | null {
  let best: string | null = null;
  for (const r of rows) {
    if (!r.scheduled_at) continue;
    if (best === null || r.scheduled_at < best) best = r.scheduled_at;
  }
  return best;
}

/**
 * Groups posts by their piece and orders them the way the queue reads:
 * soonest first, unscheduled groups after the scheduled ones (they are not
 * waiting on a date, so they should not sit among things that are), and
 * standalone posts last — they are the exception, not the lede.
 *
 * Within a group, posts keep the same ordering: scheduled by time, then the
 * unscheduled, so a group's own row order matches the reason it is placed
 * where it is.
 */
export function groupPostsByPiece<T extends SocialRowLike>(
  rows: readonly T[],
  titleOf: (pieceId: number) => string | undefined
): SocialGroup<T>[] {
  const byPiece = new Map<number | null, T[]>();
  for (const row of rows) {
    const key = row.piece_id ?? null;
    const bucket = byPiece.get(key);
    if (bucket) bucket.push(row);
    else byPiece.set(key, [row]);
  }

  const groups: SocialGroup<T>[] = [];
  for (const [pieceId, groupRows] of byPiece) {
    const sorted = [...groupRows].sort(compareRows);
    groups.push({
      pieceId,
      title:
        pieceId === null
          ? "Not tied to a piece"
          : // A piece whose title has not loaded yet is named by its id
            // rather than left blank — an unlabelled group is unreadable.
            (titleOf(pieceId) ?? `Piece #${pieceId}`),
      rows: sorted,
      earliest: earliestOf(sorted),
    });
  }

  return groups.sort((a, b) => {
    // Standalone posts last, whatever their dates.
    if (a.pieceId === null && b.pieceId !== null) return 1;
    if (b.pieceId === null && a.pieceId !== null) return -1;
    // Then unscheduled groups after scheduled ones.
    if (a.earliest === null && b.earliest !== null) return 1;
    if (b.earliest === null && a.earliest !== null) return -1;
    if (a.earliest !== null && b.earliest !== null && a.earliest !== b.earliest) {
      return a.earliest < b.earliest ? -1 : 1;
    }
    return a.title.localeCompare(b.title);
  });
}

function compareRows(a: SocialRowLike, b: SocialRowLike): number {
  if (a.scheduled_at === null && b.scheduled_at === null) return 0;
  if (a.scheduled_at === null) return 1;
  if (b.scheduled_at === null) return -1;
  if (a.scheduled_at === b.scheduled_at) return 0;
  return a.scheduled_at < b.scheduled_at ? -1 : 1;
}
